import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { useGravityToast } from '@/components/effects/GravityToast';
import { formatApiError } from '@/utils/api';
import api from '@/utils/api';
import GoldenSpiral from '@/components/effects/GoldenSpiral';
import { Lock, Eye, EyeOff, User as UserIcon } from 'lucide-react';

/**
 * "Complete your profile" step.
 * Magic-link users land here right after sign-in so they can:
 *   1. Tell us what to call them (optional if they like their current name)
 *   2. Set a password to unlock forum & full access (can be skipped)
 */
export default function SetPassword() {
  const { user, updateUser } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useGravityToast();

  // Pre-fill display name. If current display_name is the email-local-part
  // (our magic-link default) we clear it so the field feels like a fresh prompt.
  const emailLocal = (user?.email || '').split('@')[0];
  const initialName = (user?.display_name && user.display_name !== emailLocal) ? user.display_name : '';
  const [displayName, setDisplayName] = useState(initialName);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const redirectTo = location.state?.from || '/';
  const tx = (en, ar) => (lang === 'ar' ? ar : en);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError(tx('Passwords do not match', 'كلمتا المرور غير متطابقتين'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/set-password', { password, display_name: displayName });
      updateUser({ has_password: true, ...(displayName ? { display_name: displayName } : {}) });
      toast.success(tx('Profile complete! You now have full access.', 'اكتمل ملفك الشخصي! لديك الآن وصول كامل.'));
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err));
    }
    setLoading(false);
  };

  const handleSaveNameOnly = async () => {
    if (!displayName.trim()) {
      navigate(redirectTo, { replace: true });
      return;
    }
    setLoading(true);
    try {
      await api.put('/auth/profile/display-name', { display_name: displayName });
      updateUser({ display_name: displayName });
      toast.success(tx('Name saved!', 'تم حفظ الاسم!'));
    } catch {
      /* non-blocking */
    }
    setLoading(false);
    navigate(redirectTo, { replace: true });
  };

  return (
    <div data-testid="set-password-page" className="min-h-screen flex items-center justify-center px-4 py-20 relative overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <GoldenSpiral size={400} className="animate-spiral-rotate" opacity={0.04} />
      </div>

      <div className="glass-card rounded-2xl p-8 md:p-10 w-full max-w-md relative z-10" data-testid="set-password-form">
        <div className="w-14 h-14 rounded-full bg-sacred-gold/10 flex items-center justify-center mx-auto mb-5">
          <Lock size={24} className="text-sacred-gold" />
        </div>
        <h1 className="text-2xl font-display text-heading mb-2 text-center">
          {tx('Complete your profile', 'أكمل ملفك الشخصي')}
        </h1>
        <p className="text-text-muted text-sm font-body text-center mb-8">
          {tx(
            'Tell us what to call you, and optionally set a password to unlock the forum.',
            'أخبرنا كيف تودّ أن نخاطبك، ويمكنك اختيارياً إضافة كلمة مرور لفتح المنتدى.'
          )}
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-body" data-testid="set-password-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-warm-brown text-xs font-body mb-1 flex items-center gap-1.5">
              <UserIcon size={12} /> {tx('What should we call you?', 'كيف نخاطبك؟')}
            </label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              maxLength={60}
              autoFocus
              className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm"
              data-testid="display-name-input"
              placeholder={tx('Your name', 'اسمك')}
            />
          </div>

          <div className="pt-2 border-t border-sacred-gold/10">
            <p className="text-text-muted/60 text-xs font-body mb-3 mt-3">
              {tx('Optional — set a password to unlock the forum', 'اختياري — أضف كلمة مرور لفتح المنتدى')}
            </p>
            <label className="text-warm-brown text-xs font-body mb-1 block">
              {tx('New password', 'كلمة المرور الجديدة')}
            </label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                minLength={6}
                className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm pr-10"
                data-testid="new-password"
                placeholder={tx('At least 6 characters', '٦ أحرف على الأقل')}
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-dust-gray hover:text-heading">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {password && (
            <div>
              <label className="text-warm-brown text-xs font-body mb-1 block">
                {tx('Confirm password', 'تأكيد كلمة المرور')}
              </label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                minLength={6}
                required
                className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm"
                data-testid="confirm-password"
                placeholder={tx('Repeat password', 'أعد كلمة المرور')}
              />
            </div>
          )}

          {password ? (
            <button
              type="submit"
              disabled={loading}
              className="w-full gold-button px-4 py-3 rounded-full font-body text-sm disabled:opacity-50"
              data-testid="set-password-submit"
            >
              {loading ? '...' : tx('Save & unlock full access', 'حفظ وفتح الوصول الكامل')}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSaveNameOnly}
              disabled={loading}
              className="w-full gold-button px-4 py-3 rounded-full font-body text-sm disabled:opacity-50"
              data-testid="save-name-only"
            >
              {loading ? '...' : displayName ? tx('Save my name', 'حفظ الاسم') : tx('Continue', 'متابعة')}
            </button>
          )}
        </form>

        <button
          onClick={() => navigate(redirectTo, { replace: true })}
          className="w-full mt-4 text-center text-text-muted/50 text-xs font-body hover:text-text-muted"
          data-testid="skip-password"
        >
          {tx('Skip for now', 'تخطّي الآن')}
        </button>
      </div>
    </div>
  );
}
